import { useState, useEffect, useCallback, useMemo } from 'react';
import { Hotspot } from '../types';

export const isCustomizeMode = new URLSearchParams(window.location.search).get('cstm') === '1';

interface CustomHotspotData {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  description: string;
}

interface RoomCustomizeData {
  overrides: Record<string, { x: number; y: number; width: number; height: number }>;
  customHotspots: CustomHotspotData[];
}

type CustomizeStore = Record<string, RoomCustomizeData>;

// Singleton: file data loaded once and shared across all hook instances
let globalStore: CustomizeStore = {};
let globalSavedStore: CustomizeStore = {};
let globalLoaded = false;
const globalListeners = new Set<() => void>();

function notifyListeners() {
  globalListeners.forEach(l => l());
}

async function loadFromFile(): Promise<CustomizeStore> {
  // Try dev server API first, then fall back to static file (production)
  for (const url of ['/api/area-points', '/area_point.json']) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        return await res.json();
      }
    } catch {}
  }
  return {};
}

async function saveToFile(data: CustomizeStore): Promise<boolean> {
  try {
    const res = await fetch('/api/area-points', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.ok;
  } catch (e) {
    console.error('Failed to save area_point.json:', e);
    return false;
  }
}

// Load file data on module init
loadFromFile().then(data => {
  globalStore = data;
  globalSavedStore = JSON.parse(JSON.stringify(data));
  globalLoaded = true;
  notifyListeners();
});

export function useCustomizeMode(currentRoom: string) {
  const [, forceUpdate] = useState(0);

  // Subscribe to global store changes
  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    globalListeners.add(listener);
    return () => { globalListeners.delete(listener); };
  }, []);

  // Re-render when loaded
  useEffect(() => {
    if (globalLoaded) forceUpdate(n => n + 1);
  }, []);

  const store = globalStore;

  const updateStore = useCallback((newStore: CustomizeStore) => {
    globalStore = newStore;
    notifyListeners();
  }, []);

  const isDirty = useMemo(() => {
    return JSON.stringify(store[currentRoom]) !== JSON.stringify(globalSavedStore[currentRoom]);
  }, [store, currentRoom]);

  const saveCurrentRoom = useCallback(async (): Promise<boolean> => {
    const success = await saveToFile(globalStore);
    if (success) {
      globalSavedStore = JSON.parse(JSON.stringify(globalStore));
      notifyListeners();
    }
    return success;
  }, []);

  const roomData = useMemo((): RoomCustomizeData => {
    return store[currentRoom] || { overrides: {}, customHotspots: [] };
  }, [store, currentRoom]);

  const updateRoomData = useCallback((updater: (data: RoomCustomizeData) => RoomCustomizeData) => {
    const prev = globalStore;
    const newStore = {
      ...prev,
      [currentRoom]: updater(prev[currentRoom] || { overrides: {}, customHotspots: [] }),
    };
    updateStore(newStore);
  }, [currentRoom, updateStore]);

  const applyOverrides = useCallback((hotspots: Hotspot[]): Hotspot[] => {
    return hotspots.map(h => {
      const override = roomData.overrides[h.id];
      return override ? { ...h, ...override } : h;
    });
  }, [roomData]);

  const customHotspots = useMemo((): Hotspot[] => {
    return roomData.customHotspots.map(ch => ({
      id: ch.id,
      x: ch.x,
      y: ch.y,
      width: ch.width,
      height: ch.height,
      label: ch.label,
      description: ch.description,
    }));
  }, [roomData]);

  const saveBounds = useCallback((hotspotId: string, bounds: { x: number; y: number; width: number; height: number }) => {
    updateRoomData(data => {
      const customIdx = data.customHotspots.findIndex(h => h.id === hotspotId);
      if (customIdx >= 0) {
        const updated = [...data.customHotspots];
        updated[customIdx] = { ...updated[customIdx], ...bounds };
        return { ...data, customHotspots: updated };
      }
      return { ...data, overrides: { ...data.overrides, [hotspotId]: bounds } };
    });
  }, [updateRoomData]);

  const addCustomHotspot = useCallback(() => {
    const id = `custom_${currentRoom}_${Date.now()}`;
    updateRoomData(data => ({
      ...data,
      customHotspots: [...data.customHotspots, {
        id,
        x: 35,
        y: 30,
        width: 20,
        height: 25,
        label: '新しいエリア',
        description: '',
      }],
    }));
    return id;
  }, [currentRoom, updateRoomData]);

  const updateCustomHotspot = useCallback((id: string, updates: { label?: string; description?: string }) => {
    updateRoomData(data => ({
      ...data,
      customHotspots: data.customHotspots.map(h => h.id === id ? { ...h, ...updates } : h),
    }));
  }, [updateRoomData]);

  const deleteCustomHotspot = useCallback((id: string) => {
    updateRoomData(data => ({
      ...data,
      customHotspots: data.customHotspots.filter(h => h.id !== id),
    }));
  }, [updateRoomData]);

  return {
    applyOverrides,
    customHotspots,
    saveBounds,
    addCustomHotspot,
    updateCustomHotspot,
    deleteCustomHotspot,
    isDirty,
    saveCurrentRoom,
  };
}
